import { Fragment, memo, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  GridLegacy,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import LogoSection from 'layout/MainLayout/LogoSection';
import { activeItem, openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';
import cofactrLogo from 'assets/images/cofactr_logo.png';
import cofactrSmallLogo from 'assets/images/Cofactr_logomark_clover_256x256.png';
import {
  drawerWidth,
  collapsedDrawerWidth,
  actionButtonSpacing,
  roundedButtonAvatarSx,
} from 'constants/themeConstants';
import { setViewAsStaff, useGetUserQuery } from 'store/slices/apiV1/user';
import {
  IconBug,
  IconDashboard,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconUserShield,
  IconWorldShare,
} from '@tabler/icons-react';
import ActiveOrgSwitcher from 'layout/MainLayout/Header/ActiveOrgSwitcher';
import _ from 'lodash';
import useMenuItems from 'layout/MainLayout/Sidebar/useMenuItems';
import NavGroup from 'layout/MainLayout/Sidebar/MenuList/NavGroup';
import NavCollapse from 'layout/MainLayout/Sidebar/MenuList/NavCollapse';
import NavItem from 'layout/MainLayout/Sidebar/MenuList/NavItem';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { appPermissionStates } from 'types/apps';
import config from 'config';
import { useNavigate } from 'react-router-dom';
import ProfileSection from 'layout/MainLayout/Header/ProfileSection';
import KnockNotificationWidget from 'ui-component/third-party/KnockNotificationWidget';
import { IS_KNOCK_PROD } from 'constants/envConstants';
import { isCofactrUser } from 'utils/userPermissions';
import BugReportDialog from 'ui-component/BugReportDialog';
import AppButton from 'layout/MainLayout/Sidebar/AppButton';
import { useNewVersion } from 'contexts/NewVersionContext';
import { DASHBOARD_ROUTE } from 'routes/constants';
import Chip from 'ui-component/extended/Chip';
import { QueryFilterOperators, ALL } from 'types/api';
import useServerSideQuery from 'hooks/useServerSideQuery';
import {
  PunchoutSessionSchema,
  PunchoutSessionSchemaLite,
} from 'types/clientV2/punchout';
import { useGetPunchoutSessionsQuery } from 'store/slices/clientV2/punchoutSessions';
import { skipToken } from '@reduxjs/toolkit/query';
import { useConfirm } from 'material-ui-confirm';
import {
  cofactrName,
  cancellationButtonProps,
  confirmationButtonProps,
  ALL_APP_IDS,
} from 'constants/appConstants';
import { setActiveOrgId } from 'store/slices/apiV1/org';
import GlobalSearch from 'ui-component/GlobalSearch';
import { createSxWithoutUndefined } from 'utils/functions';
import ActiveCartsSummaryWidget from 'views/simplifiedPurchasing/ActiveCartsSummaryWidget';

interface SidebarProps {
  window?: Window;
}

const { borderRadius } = config;

const Sidebar = ({ window }: SidebarProps) => {
  const theme = useTheme();
  const { activeOrgId, activeOrg, punchoutId } = useSelector(
    (state) => state.org
  );
  const confirm = useConfirm();
  const dispatch = useDispatch();
  const { drawerOpen, openItem } = useSelector((state) => state.menu);
  const menuItems = useMenuItems();
  const { appsWithPermissions, hasAppPermission } = useAppAccessContext();
  const { isSuccess: isGetUserSuccess, data: user } = useGetUserQuery();
  const navigate = useNavigate();
  const { isLoggedIn } = useSelector(({ session }) => session);
  const { viewAsStaff } = useSelector((state) => state.user);
  const isCofactr = isCofactrUser({ user, viewAsStaff });
  const { showNoPlanBanner } = useSelector((state) => state.utility);
  const { showNewVersion } = useNewVersion();
  const hasSimplifiedBuying = hasAppPermission(
    ALL_APP_IDS.SIMPLIFIED_BUYING_ORDERS
  );

  const { data: punchoutSessionResponse } = useServerSideQuery<
    PunchoutSessionSchema,
    PunchoutSessionSchemaLite
  >(
    useGetPunchoutSessionsQuery,
    punchoutId
      ? {
          pageSize: 1,
          schema: [ALL],
          filters: [
            {
              field: 'id',
              operator: QueryFilterOperators['='],
              value: punchoutId,
            },
          ],
        }
      : skipToken
  );

  const handleEndPunchout = () => {
    confirm({
      title: 'Exit Punchout?',
      description:
        'Are you sure that you want to exit the punchout session? To re-start punchout, you will need to start a new session from your procurement system.',
      confirmationText: 'Yes',
      cancellationButtonProps,
      confirmationButtonProps,
    }).then(async () => {
      dispatch(
        setActiveOrgId({
          orgId: activeOrgId,
          org: activeOrg,
          skipResetState: true,
          blended: false,
          sandbox: false,
          punchoutId: null,
        })
      );
    });
  };

  const orgOptions = useMemo(() => {
    if (!user || user?.orgs.length < 2) return [];
    return _.chain(user?.orgs)
      .filter((o) => !o.createdFrom)
      .sortBy('name')
      .map((o) => [
        o,
        ..._.chain(user?.orgs)
          .filter(
            (co) =>
              (typeof co.createdFrom === 'string'
                ? co.createdFrom
                : co.createdFrom?.id) === o.id
          )
          .value(),
      ])
      .flatten()
      .value();
  }, [user?.orgs]);

  const navApps = useMemo(
    () =>
      _.chain(appsWithPermissions)
        .values()
        .filter(
          (a) =>
            (a.includeInSidebar || false) &&
            a.permissions?.state === appPermissionStates.enabled
        )
        .value(),
    [appsWithPermissions]
  );

  const itemHandler = (id: string) => {
    dispatch(activeItem([id]));
  };

  const navSection = useMemo(
    () => (
      <PerfectScrollbar component="div">
        <Stack
          direction="column"
          sx={{
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            p: 2,
          }}
        >
          {drawerOpen &&
            menuItems.items.map((item) => {
              switch (item.type) {
                case 'group':
                  return item?.children && item.children.length > 0 ? (
                    <NavGroup key={item.id} item={item} />
                  ) : null;
                case 'collapse':
                  return <NavCollapse key={item.id} menu={item} level={1} />;
                default:
                  return <NavItem key={item.id} item={item} level={1} />;
              }
            })}
          {!drawerOpen && (
            <>
              <Tooltip title="Dashboard">
                <IconButton
                  sx={{
                    borderRadius: `${borderRadius}px`,
                    width: '42px',
                    height: '42px',
                    backgroundColor: _.includes(openItem, 'dashboard')
                      ? theme.palette.brand.moss
                      : 'transparent',
                    color: _.includes(openItem, 'dashboard')
                      ? theme.palette.primary.main
                      : undefined,
                  }}
                  onClick={() => {
                    navigate(DASHBOARD_ROUTE);
                    itemHandler('dashboard');
                  }}
                >
                  <IconDashboard stroke={1.5} size="1.3rem" />
                </IconButton>
              </Tooltip>
              {_.filter(navApps, (a) => !a.appGroup).map((app) => (
                <AppButton key={app.id} app={app} />
              ))}
              {_.chain(navApps)
                .filter((a) => !!a.appGroup)
                .groupBy('appGroup.id')
                .values()
                .value()
                .map((apps) => (
                  <Fragment key={apps[0].appGroup?.id}>
                    <Divider flexItem sx={{ my: 1 }} />
                    {apps.map((app) => (
                      <AppButton key={app.id} app={app} />
                    ))}
                  </Fragment>
                ))}
            </>
          )}
        </Stack>
      </PerfectScrollbar>
    ),
    [drawerOpen, navApps, openItem]
  );

  const renderStaffUserToggle =
    _.find(user?.orgs, ['id', user?.org])?.name === cofactrName;

  const [bugReportDialogOpen, setBugReportDialogOpen] = useState(false);

  const selectedApp = _.find(
    appsWithPermissions,
    (a) =>
      _.includes(openItem, a.id as string) ||
      _.some(_.keys(a.views || {}), (key) => _.includes(openItem, key))
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { md: 0 },
        width: drawerOpen ? drawerWidth : collapsedDrawerWidth,
      }}
    >
      <Drawer
        variant="permanent"
        anchor="left"
        open={drawerOpen}
        onClose={() => dispatch(openDrawer(!drawerOpen))}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerOpen ? drawerWidth : collapsedDrawerWidth,
            background: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRight: 'none',
            height: '100%',
            mt: showNewVersion || showNoPlanBanner ? '30px' : 0,
          },
        }}
        ModalProps={{ keepMounted: true }}
        color="inherit"
      >
        <Box
          sx={{
            position: 'fixed',
            top: '16px',
            left: `${
              (drawerOpen ? drawerWidth : collapsedDrawerWidth) - 34 / 2
            }px`,
            zIndex: 100000,
          }}
        >
          <Avatar
            variant="rounded"
            sx={createSxWithoutUndefined(roundedButtonAvatarSx(theme))}
            onClick={() => dispatch(openDrawer(!drawerOpen))}
            color="inherit"
          >
            {drawerOpen ? (
              <IconLayoutSidebarLeftCollapse stroke={1.5} size="1.3rem" />
            ) : (
              <IconLayoutSidebarLeftExpand stroke={1.5} size="1.3rem" />
            )}
          </Avatar>
        </Box>
        <GridLegacy
          container
          direction="column"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'stretch',
            height: '100%',
          }}
        >
          {drawerOpen && (
            <GridLegacy item xs="auto" sx={{ pb: 2, pl: 2, pt: '12px', pr: 3 }}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <LogoSection />
                {!_.includes(openItem, 'dashboard') && !!selectedApp && (
                  <AppButton app={selectedApp} currentAppVariant />
                )}
              </Stack>
            </GridLegacy>
          )}
          {activeOrgId && orgOptions.length > 0 && drawerOpen && (
            <GridLegacy item xs="auto" sx={{ p: 2 }}>
              <ActiveOrgSwitcher />
            </GridLegacy>
          )}
          {!drawerOpen &&
            !_.includes(openItem, 'dashboard') &&
            !!selectedApp && (
              <GridLegacy item xs="auto" sx={{ pl: 1, pt: '12px', pb: 0 }}>
                <AppButton app={selectedApp} currentAppVariant />
              </GridLegacy>
            )}
          {!drawerOpen &&
            (_.includes(openItem, 'dashboard') || !selectedApp) && (
              <GridLegacy item xs="auto" sx={{ pl: 1, pt: '12px', pb: 0 }}>
                <Box sx={{ height: '42px' }} />
              </GridLegacy>
            )}
          {hasSimplifiedBuying && (
            <GridLegacy item xs="auto" sx={{ pl: 1, pt: '12px', pb: 0 }}>
              <ActiveCartsSummaryWidget />
            </GridLegacy>
          )}
          {!hasSimplifiedBuying && isGetUserSuccess && (
            <GridLegacy
              item
              xs="auto"
              sx={{ px: 2, pb: drawerOpen ? 2 : 0, pt: drawerOpen ? 0 : 2 }}
            >
              <GlobalSearch user={user} />
            </GridLegacy>
          )}
          {drawerOpen && <Divider flexItem />}
          <GridLegacy item xs sx={{ flexGrow: 1, overflowY: 'hidden' }}>
            {navSection}
          </GridLegacy>
          <Divider flexItem />
          {!!punchoutId && !!punchoutSessionResponse?.data && drawerOpen && (
            <Chip
              label="Punchout"
              color="success"
              avatar={<IconWorldShare color={theme.palette.success.main} />}
              sx={{ mx: 2, mt: 2 }}
              onDelete={handleEndPunchout}
            />
          )}
          <GridLegacy item xs="auto" sx={{ p: 2 }}>
            <Stack
              direction={drawerOpen ? 'row' : 'column-reverse'}
              spacing={actionButtonSpacing}
              sx={
                drawerOpen
                  ? {
                      justifyContent: 'center',
                      alignItems: 'center',
                    }
                  : {
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }
              }
            >
              <ProfileSection />
              {activeOrgId && isLoggedIn && (
                <Box>
                  <KnockNotificationWidget authWithKnock={IS_KNOCK_PROD} />
                </Box>
              )}
              {isCofactr && (
                <Tooltip title="Report Bug">
                  <Avatar
                    variant="rounded"
                    sx={createSxWithoutUndefined(roundedButtonAvatarSx(theme))}
                    onClick={() => setBugReportDialogOpen(true)}
                    color="inherit"
                  >
                    <IconBug stroke={1.5} size="1.3rem" />
                  </Avatar>
                </Tooltip>
              )}

              {renderStaffUserToggle && (
                <Tooltip
                  title={
                    viewAsStaff ? 'Disable staff mode' : 'Enable staff mode'
                  }
                >
                  <Avatar
                    variant="rounded"
                    sx={createSxWithoutUndefined({
                      ...roundedButtonAvatarSx(theme),
                      ...{
                        background: viewAsStaff
                          ? theme.palette.success.main
                          : theme.palette.brand.moss,
                        color: viewAsStaff
                          ? theme.palette.common.white
                          : theme.palette.brand.steel,
                      },
                    })}
                    onClick={() => {
                      dispatch(setViewAsStaff(!viewAsStaff));
                    }}
                    color="inherit"
                  >
                    <IconUserShield stroke={1.5} size="1.3rem" />
                  </Avatar>
                </Tooltip>
              )}
            </Stack>
          </GridLegacy>
          <GridLegacy
            item
            xs="auto"
            sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'center' }}
          >
            {drawerOpen && (
              <img src={cofactrLogo} alt="Cofactr Logo" width="100" />
            )}
            {!drawerOpen && (
              <img src={cofactrSmallLogo} alt="Cofactr Logo" width="24" />
            )}
          </GridLegacy>
        </GridLegacy>
      </Drawer>
      {bugReportDialogOpen && (
        <BugReportDialog
          open={bugReportDialogOpen}
          onClose={() => setBugReportDialogOpen(false)}
        />
      )}
    </Box>
  );
};

export default memo(Sidebar);
